import { useNavigate, useLocation } from 'react-router-dom';
import { useMediaQuery, useTheme } from '@mui/material';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Divider,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Computer as RaspiIcon,
  Science as ExperimentsIcon,
  Logout as LogoutIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { LAYOUT } from '@/constants/layout';
import { useLayoutStore } from '@/store/layoutStore';
import { useAuthStore } from '@/store/authStore';
import { useUnauthorizedAction } from '../../App';

const NavLeftPanel = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  
  // Shared layout state
  const { isDrawerCollapsed, toggleDrawer } = useLayoutStore();
  const { logout } = useAuthStore();
  const getRole = useAuthStore((state) => state.getRole);
  const isViewer = getRole() === 'viewer' || getRole() === 'public';
  const triggerUnauthorized = useUnauthorizedAction();
  
  // Check if screen is small (mobile/tablet)
  const isSmallScreen = useMediaQuery('(max-width:1400px)'); // Collapse at 1400px

  const menuItems = [
    { text: 'DASHBOARD', icon: <DashboardIcon />, path: '/' },
    { text: 'RASPI Details', icon: <RaspiIcon />, path: '/raspi-details' },
    { text: 'EXPERIMENTS', icon: <ExperimentsIcon />, path: '/experiments' },
  ];

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Calculate drawer width based on screen size and manual collapse
  const isActuallyCollapsed = isSmallScreen || isDrawerCollapsed;
  const drawerWidth = isActuallyCollapsed ? 64 : LAYOUT.DRAWER_WIDTH;

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          backgroundColor: 'background.paper',
          borderRight: '1px solid',
          borderColor: 'divider',
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        },
      }}
    >
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        height: '100%'
      }}>
        <Box sx={{ 
          p: isActuallyCollapsed ? 1 : LAYOUT.PADDING.MEDIUM / 8,
          display: 'flex', 
          justifyContent: 'center',
          alignItems: 'center',
          height: LAYOUT.APP_BAR_HEIGHT,
          position: 'relative'
        }}>
          <img 
            src="/iop_logo.png" 
            alt="IOP Logo" 
            style={{ 
              height: isActuallyCollapsed ? '32px' : '40px',
              width: 'auto'
            }} 
          />
          {/* Toggle button - only show on larger screens */}
          {!isSmallScreen && (
            <IconButton
              onClick={toggleDrawer}
              sx={{
                position: 'absolute',
                right: -12,
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                width: 24,
                height: 24,
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
                zIndex: 1,
              }}
            >
              {isDrawerCollapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
            </IconButton>
          )}
        </Box>
        <Divider />
        <List>
          {menuItems.map((item) => (
            <ListItem key={item.text} disablePadding>
              <Tooltip 
                title={isActuallyCollapsed ? item.text : ''} 
                placement="right"
                disableHoverListener={!isActuallyCollapsed}
              >
                <ListItemButton
                  selected={location.pathname === item.path}
                  onClick={() => handleNavigation(item.path)}
                  sx={{
                    minHeight: isActuallyCollapsed ? 48 : 56,
                    justifyContent: isActuallyCollapsed ? 'center' : 'flex-start',
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'primary.contrastText',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                      '& .MuiListItemIcon-root': {
                        color: 'primary.contrastText',
                      },
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: location.pathname === item.path ? 'primary.contrastText' : 'inherit',
                      minWidth: isActuallyCollapsed ? 'auto' : 40,
                      justifyContent: 'center',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {!isActuallyCollapsed && (
                    <ListItemText 
                      primary={item.text} 
                      sx={{
                        '& .MuiListItemText-primary': {
                          fontSize: '0.875rem',
                          fontWeight: 'medium',
                        }
                      }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          ))}
        </List>
        <Box sx={{ flexGrow: 1 }} />
        <Divider />
        <List>
          <ListItem disablePadding>
            <Tooltip 
              title={isActuallyCollapsed ? 'LOGOUT' : ''} 
              placement="right"
              disableHoverListener={!isActuallyCollapsed}
            >
              <ListItemButton
                onClick={isViewer ? () => triggerUnauthorized() : handleLogout}
                sx={{
                  minHeight: isActuallyCollapsed ? 48 : 56,
                  justifyContent: isActuallyCollapsed ? 'center' : 'flex-start',
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: isActuallyCollapsed ? 'auto' : 40,
                    justifyContent: 'center',
                  }}
                >
                  <LogoutIcon />
                </ListItemIcon>
                {!isActuallyCollapsed && (
                  <ListItemText 
                    primary="LOGOUT" 
                    sx={{
                      '& .MuiListItemText-primary': {
                        fontSize: '0.875rem',
                        fontWeight: 'medium',
                      }
                    }}
                  />
                )}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        </List>
      </Box>
    </Drawer>
  );
};

export default NavLeftPanel;
